import { queryClient, trpc } from "~/utils/trpc";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@quake/shared-ui/components/ui/tooltip";
import { Label } from "@radix-ui/react-label";
import { DataTable } from "@quake/shared-ui/components/dataTable/dataTable";
import type { FilterFields } from "@quake/shared-ui/components/dataTable/dataTableFilter";
import { Button } from "@quake/shared-ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@quake/shared-ui/components/ui/tabs";
import { Checkbox } from "@quake/shared-ui/components/ui/checkbox";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import type { inferOutput } from "@trpc/tanstack-react-query";
import { Outlet, Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import UsersIcon from "lucide-react/dist/esm/icons/users";
import UserCheckIcon from "lucide-react/dist/esm/icons/user-check";
import LayersIcon from "lucide-react/dist/esm/icons/layers";
import UserCogIcon from "lucide-react/dist/esm/icons/user-cog";
import UserXIcon from "lucide-react/dist/esm/icons/user-x";
import FingerprintIcon from "lucide-react/dist/esm/icons/fingerprint";
import { useEffect, useMemo, useState } from "react";
import { toast } from "@quake/shared-ui/lib/sonner";
import { Input } from "@quake/shared-ui/components/ui/input";
import { SearchInput } from "~/components/dataTableSearchInput";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import { Badge } from "@quake/shared-ui/components/ui/badge";
import { AccessGroupsSettingsTab } from "~/pages/AccessGroupsSettingsTab";
import { checkPermission } from "~/store/usePermissionStore";

export const Route = createFileRoute("/_dashboard/$datasetSlug/settings/users")({
  component: RouteComponent,
  validateSearch: z.object({ view: z.string().optional() }),
  loader: async () => {
    const roles = await queryClient.fetchQuery(trpc.permissions.getRoles.queryOptions());
    return { roles };
  },
});

type DatasetUserRow = inferOutput<typeof trpc.users.getUsersByDatasets>["data"][number];
type UserStatusFilter = "active" | "disabled";

function getSelectedFilterIds(filters: ColumnFiltersState, id: string) {
  const value = filters.find((filter) => filter.id === id)?.value;
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function isUserStatusFilter(value: string): value is UserStatusFilter {
  return value === "active" || value === "disabled";
}

function isDatasetUserActive(user: Pick<DatasetUserRow, "active" | "dataset_active">) {
  return user.active !== false && user.dataset_active !== false;
}

function RouteComponent() {
  const { roles } = Route.useLoaderData();
  const qc = useQueryClient();
  const { datasetSlug } = Route.useParams();
  const { view } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [newUser, setNewUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role_id: "",
    deactivateAccess: false,
    sendInvitation: true,
  });
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [page, setPage] = useState({
    size: 100,
    currentPage: 1,
    totalRows: 0,
    tableOffset: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterValues, setFilterValues] = useState<ColumnFiltersState>([]);
  const [selectedRows, setSelectedRows] = useState<DatasetUserRow[]>([]);
  const roleFilterIds = useMemo(() => getSelectedFilterIds(filterValues, "role_id"), [filterValues]);
  const accessGroupFilterIds = useMemo(() => getSelectedFilterIds(filterValues, "access_group_ids"), [filterValues]);
  const statusFilterIds = useMemo(
    () => getSelectedFilterIds(filterValues, "status").filter(isUserStatusFilter),
    [filterValues],
  );

  // User group assignment dialog state
  const [groupAssignDialogOpen, setGroupAssignDialogOpen] = useState(false);
  const [assigningUser, setAssigningUser] = useState<{
    id: string;
    name: string | null;
    email: string;
    invitation: boolean;
  } | null>(null);
  const [selectedPrimary, setSelectedPrimary] = useState<string | null>(null);
  const [selectedSecondary, setSelectedSecondary] = useState<string[]>([]);

  const canViewAccessGroups = checkPermission("access_groups", "view");
  const canAssign = checkPermission("access_groups", "assign");
  const canBulkChangeAccess = checkPermission("users", "deactivate");
  const canUpdateUsers = checkPermission("users", "update");

  const { data, status } = useQuery(
    trpc.users.getUsersByDatasets.queryOptions({
      pagination: {
        page: page.currentPage,
        pageSize: page.size,
      },
      search: searchTerm.trim() || undefined,
      role_ids: roleFilterIds,
      access_group_ids: accessGroupFilterIds,
      statuses: statusFilterIds,
    }),
  );

  const { data: accessGroupsSettings } = useQuery({
    ...trpc.accessGroups.getSettings.queryOptions(),
    enabled: !!canViewAccessGroups,
  });

  const singularDisplayLabel = accessGroupsSettings?.display_label ?? "Access Group";
  const pluralDisplayLabel = accessGroupsSettings?.display_label_plural ?? "Access Groups";

  // Fetch assignments for the selected user (only when dialog is open and user is verified)
  const { data: assignmentData, isLoading: assignmentLoading } = useQuery({
    ...trpc.users.getAccessGroupAssignments.queryOptions({
      user_dataset_id: assigningUser?.id ?? "",
    }),
    enabled: groupAssignDialogOpen && !!assigningUser?.id && !!assigningUser?.invitation,
  });

  const { data: allGroups = [] } = useQuery({
    ...trpc.accessGroups.list.queryOptions(),
    enabled: !!canViewAccessGroups,
  });

  // Sync form state when assignment data arrives
  useEffect(() => {
    if (assignmentData) {
      setSelectedPrimary(assignmentData.primary_access_group_id ?? null);
      setSelectedSecondary(assignmentData.secondary_access_group_ids);
    } else if (!groupAssignDialogOpen) {
      setSelectedPrimary(null);
      setSelectedSecondary([]);
    }
  }, [assignmentData, groupAssignDialogOpen]);

  const [currentUser, setCurrentUser] = useState<DatasetUserRow | null>(null);

  const removeUser = useMutation(
    trpc.datasets.removeUser.mutationOptions({
      onSuccess: () => {
        setRemoveUserDialogOpen(false);
        qc.invalidateQueries(trpc.permissions.getRoles.queryFilter());
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
      },
      onError: (error) => {
        setRemoveUserDialogOpen(false);
        toast.error(error.message);
      },
    }),
  );

  const addUser = useMutation(
    trpc.datasets.addUser.mutationOptions({
      onSuccess: () => {
        setAddUserDialogOpen(false);
        setNewUser({
          first_name: "",
          last_name: "",
          email: "",
          role_id: "",
          deactivateAccess: false,
          sendInvitation: true,
        });
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
        toast.success(newUser.deactivateAccess ? "User added with deferred access." : "Invitation sent.");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const updateUserRole = useMutation(
    trpc.permissions.assignRoleToUser.mutationOptions({
      onSuccess: () => {
        toast.success("Role updated");
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
      },
      onError: (error) => {
        toast.error(error.message);
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
      },
    }),
  );

  const setAccessGroupAssignments = useMutation(
    trpc.users.setAccessGroupAssignments.mutationOptions({
      onSuccess: () => {
        toast.success("Group assignments updated");
        setGroupAssignDialogOpen(false);
        setAssigningUser(null);
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const bulkActivateUser = useMutation(trpc.datasets.activateUser.mutationOptions());
  const bulkDeactivateUser = useMutation(trpc.datasets.deactivateUser.mutationOptions());
  const isBulkAccessPending = bulkActivateUser.isPending || bulkDeactivateUser.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    addUser.mutate({
      email: newUser.email,
      first_name: newUser.first_name || undefined,
      last_name: newUser.last_name || undefined,
      role_id: newUser.role_id,
      deferred_access: newUser.deactivateAccess,
      send_invitation: newUser.sendInvitation,
    });
  };

  const handleSaveGroupAssignments = () => {
    if (!assigningUser) return;
    setAccessGroupAssignments.mutate({
      user_dataset_id: assigningUser.id,
      primary_access_group_id: selectedPrimary ?? null,
      secondary_access_group_ids: selectedSecondary,
    });
  };

  const openGroupAssignDialog = (user: { id: string; name: string | null; email: string; invitation: boolean }) => {
    setAssigningUser(user);
    setSelectedPrimary(null);
    setSelectedSecondary([]);
    setGroupAssignDialogOpen(true);
  };

  const toggleSecondary = (groupId: string) => {
    setSelectedSecondary((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };

  useEffect(() => {
    if (data?.total !== page.totalRows && data) {
      setPage((prev) => ({ ...prev, totalRows: data?.total }));
    }
  }, [data?.total]);

  const resetToFirstPage = () => {
    setPage((prev) => ({
      ...prev,
      currentPage: 1,
      tableOffset: 0,
    }));
  };

  const handleSearchValueChange = (value: string) => {
    setSearchTerm(value);
    setSelectedRows([]);
    resetToFirstPage();
  };

  const handleFilterChange = (nextFilterValues: ColumnFiltersState) => {
    setFilterValues(nextFilterValues);
    setSelectedRows([]);
    resetToFirstPage();
  };

  const handleBulkAccessChange = async (action: "activate" | "deactivate") => {
    if (isBulkAccessPending) return;

    const targetRows = selectedRows.filter((row) =>
      action === "activate" ? !isDatasetUserActive(row) : isDatasetUserActive(row),
    );
    const skippedCount = selectedRows.length - targetRows.length;

    if (targetRows.length === 0) {
      toast.info(action === "activate" ? "Selected users are already active." : "Selected users are already inactive.");
      return;
    }

    const results = await Promise.allSettled(
      targetRows.map((row) =>
        action === "activate"
          ? bulkActivateUser.mutateAsync({ user_dataset_id: row.id, send_welcome_email: false })
          : bulkDeactivateUser.mutateAsync({ user_dataset_id: row.id }),
      ),
    );
    const failedCount = results.filter((result) => result.status === "rejected").length;
    const successCount = results.length - failedCount;

    await qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
    setSelectedRows([]);

    const verb = action === "activate" ? "Activated" : "Deactivated";
    const skippedText = skippedCount > 0 ? ` Skipped ${skippedCount}.` : "";

    if (failedCount === 0) {
      toast.success(`${verb} ${successCount} user${successCount === 1 ? "" : "s"}.${skippedText}`);
      return;
    }

    if (successCount > 0) {
      toast.error(`${verb} ${successCount}; ${failedCount} failed.${skippedText}`);
      return;
    }

    toast.error(`Failed to ${action} selected users.${skippedText}`);
  };

  const filterFields = useMemo<FilterFields>(() => {
    const fields: FilterFields = [];

    if (canViewAccessGroups) {
      fields.push({
        id: "access_group_ids",
        label: pluralDisplayLabel,
        type: "multi-select",
        options: allGroups.map((group) => ({ label: group.name, value: group.id })),
      });
    }

    fields.push({
      id: "status",
      label: "Status",
      type: "multi-select",
      options: [
        { label: "Active", value: "active" },
        { label: "Disabled", value: "disabled" },
      ],
    });

    fields.push({
      id: "role_id",
      label: "Role",
      type: "multi-select",
      options: roles.map((role) => ({ label: role.name, value: role.id })),
    });

    return fields;
  }, [allGroups, canViewAccessGroups, pluralDisplayLabel, roles]);

  const columns: ColumnDef<DatasetUserRow>[] = [
    {
      header: "Email",
      accessorKey: "email",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="max-md:min-w-0 max-md:break-all">{row.original.email}</span>
          {row.original.mfa_enabled ? (
            <Badge
              variant="outline"
              className="border-emerald-200 bg-emerald-50 px-1.5 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
              title="MFA enabled"
            >
              <FingerprintIcon className="h-3.5 w-3.5" />
            </Badge>
          ) : null}
        </div>
      ),
    },
    { header: "Name", accessorKey: "name" },
    {
      id: "access_group_ids",
      header: pluralDisplayLabel,
      accessorFn: (row) => [
        ...(row.primary_access_groups ? [row.primary_access_groups.id] : []),
        ...row.secondary_access_groups.map((group) => group.id),
      ],
      cell: ({ row }) => {
        const { primary_access_groups, secondary_access_groups } = row.original;
        return (
          <div className="flex flex-wrap gap-1.5">
            {primary_access_groups ? (
              <Tooltip>
                <TooltipTrigger>
                  <Badge className="flex items-center gap-1">
                    <LayersIcon className="size-3" />
                    {primary_access_groups.name}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Primary {singularDisplayLabel}</TooltipContent>
              </Tooltip>
            ) : null}
            {secondary_access_groups.map((group) => (
              <Tooltip key={group.id}>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-secondary text-secondary-foreground flex items-center gap-1">
                    <LayersIcon className="size-3" />
                    {group.name}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Secondary {pluralDisplayLabel}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => (isDatasetUserActive(row) ? "active" : "disabled"),
      cell: ({ row }) => {
        if (!isDatasetUserActive(row.original)) {
          return <Badge variant="destructive">Disabled</Badge>;
        }
        return <Badge variant="default">Active</Badge>;
      },
    },
    {
      id: "role_id",
      header: "Role",
      accessorFn: (row) => row.role_id ?? "",
      cell: ({ row }) =>
        updateUserRole.isPending ? (
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 motion-preset-spin" />
          </div>
        ) : (
          <Select
            disabled={updateUserRole.isPending}
            onValueChange={(e) => {
              updateUserRole.mutate({ user_id: row.original.id, roleId: e });
            }}
            defaultValue={row.original.role_id || ""}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Role" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        <TooltipProvider>
          <div className="flex max-md:flex-wrap">
            {canUpdateUsers ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    aria-label="Edit user"
                    className="cursor-pointer max-md:justify-center max-md:min-w-11"
                    to="/$datasetSlug/settings/users/$id/edit"
                    params={{ datasetSlug, id: row.original.id }}
                  >
                    <EditIcon size={20} className="mr-2 text-blue-500 cursor-pointer" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
            ) : null}
            {canAssign && row.original.invitation && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label="Assign Access Groups"
                    className="mr-2 text-purple-500 cursor-pointer max-md:flex max-md:size-11 max-md:items-center max-md:justify-center"
                    onClick={() =>
                      openGroupAssignDialog({
                        id: row.original.id,
                        name: row.original.name,
                        email: row.original.email,
                        invitation: row.original.invitation,
                      })
                    }
                  >
                    <UserCogIcon size={20} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Assign Access Groups</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label="Remove User"
                  className="mr-2 text-red-500 cursor-pointer max-md:flex max-md:size-11 max-md:items-center max-md:justify-center"
                  onClick={() => {
                    setCurrentUser(row.original);
                    setRemoveUserDialogOpen(true);
                  }}
                >
                  <Trash2Icon size={20} />
                </button>
              </TooltipTrigger>
              <TooltipContent>Remove User</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
    },
  ];

  useBreadCrumbs([{ title: "Users", link: `/${datasetSlug}/settings/users` }]);

  const activeTab = view ?? "users";

  return (
    <div>
      <Tabs
        value={activeTab}
        onValueChange={(val) => navigate({ search: { view: val === "users" ? undefined : val } })}
      >
        <div className="border-b mb-4 max-md:overflow-x-auto">
          <TabsList className="h-12 bg-transparent border-0 rounded-none justify-start gap-0 p-0">
            <TabsTrigger
              value="users"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4"
            >
              <UsersIcon className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger
              value="access-groups"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4"
            >
              <LayersIcon className="h-4 w-4 mr-2" />
              {pluralDisplayLabel}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Users tab */}
        <TabsContent value="users">
          <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <SearchInput value={searchTerm} onValueChange={handleSearchValueChange} placeholder="Search users..." />
            <Button
              className="md:self-auto"
              onClick={() => {
                setNewUser({
                  first_name: "",
                  last_name: "",
                  email: "",
                  role_id: "",
                  deactivateAccess: false,
                  sendInvitation: true,
                });
                setAddUserDialogOpen(true);
              }}
            >
              Add User
            </Button>
          </div>
          <DataTable<DatasetUserRow>
            mobileLayout="cards"
            getRowLabel={(user) => user.email}
            data={data?.data ?? []}
            isLoading={status === "pending"}
            showPagination={true}
            pageLimit={page.size}
            pageOffset={page.tableOffset}
            rowCount={page.totalRows}
            showSearchFilter={false}
            showColumnFilter
            columns={columns}
            filterFields={filterFields}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            manualSorting={true}
            selectedRows={selectedRows.map((row) => row.id)}
            setSelectedRows={(_, rows) => setSelectedRows(rows)}
            multiSelectFunction={
              canBulkChangeAccess
                ? [
                    <Button
                      key="bulk-activate-access"
                      variant="outline"
                      className="mr-2"
                      disabled={isBulkAccessPending}
                      onClick={() => void handleBulkAccessChange("activate")}
                    >
                      <UserCheckIcon className="mr-2 size-4" />
                      Grant Access
                    </Button>,
                    <Button
                      key="bulk-deactivate-access"
                      variant="destructive"
                      className="mr-2"
                      disabled={isBulkAccessPending}
                      onClick={() => void handleBulkAccessChange("deactivate")}
                    >
                      <UserXIcon className="mr-2 size-4" />
                      Revoke Access
                    </Button>,
                  ]
                : undefined
            }
            onPageChange={({ pageOffset }) => {
              setPage((prev) => ({
                ...prev,
                tableOffset: pageOffset,
                currentPage: Math.floor(pageOffset / page.size) + 1,
              }));
            }}
          />
        </TabsContent>

        {/* Access Groups tab */}
        <TabsContent value="access-groups">
          <AccessGroupsSettingsTab displayLabel={pluralDisplayLabel} />
        </TabsContent>
      </Tabs>

      {/* Remove user dialog */}
      <Dialog open={removeUserDialogOpen} onOpenChange={setRemoveUserDialogOpen}>
        <DialogContent className="max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>Remove User From Dataset</DialogTitle>
            <DialogDescription>Are you sure you want to remove this user from the current dataset?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="max-md:gap-2">
            <Button variant="outline" onClick={() => setRemoveUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (currentUser) {
                  removeUser.mutateAsync({ user_id: currentUser?.user_id, dataset_id: currentUser?.dataset_id || "" });
                }
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User dialog */}
      <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
        <DialogContent className="max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>Add User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="flex flex-col w-full col-span-1 gap-1">
              <Label htmlFor="first_name">
                First Name {!newUser.deactivateAccess && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="first_name"
                type="text"
                placeholder="First Name"
                value={newUser.first_name}
                onChange={(e) => setNewUser((prev) => ({ ...prev, first_name: e.target.value }))}
                required={!newUser.deactivateAccess}
              />
            </div>
            <div className="flex flex-col w-full col-span-1 gap-1">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                type="text"
                placeholder="Last Name"
                value={newUser.last_name}
                onChange={(e) => setNewUser((prev) => ({ ...prev, last_name: e.target.value }))}
              />
            </div>
            <div className="flex flex-col w-full col-span-full gap-1">
              <Label htmlFor="email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="flex flex-col w-full col-span-full gap-1">
              <Label htmlFor="role">
                Role <span className="text-destructive">*</span>
              </Label>
              <Select
                value={newUser.role_id}
                onValueChange={(val) => setNewUser((prev) => ({ ...prev, role_id: val }))}
              >
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-full flex flex-col gap-1 pt-1">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="deactivate_access"
                  checked={newUser.deactivateAccess}
                  onCheckedChange={(checked) => setNewUser((prev) => ({ ...prev, deactivateAccess: !!checked }))}
                />
                <Label htmlFor="deactivate_access" className="cursor-pointer font-medium">
                  Deactivate Access
                </Label>
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                User will be added to the system but won't be able to log in. You can grant access at any time.
              </p>
            </div>
            {!newUser.deactivateAccess && (
              <div className="col-span-full flex items-center gap-2">
                <Checkbox
                  id="send_invitation"
                  checked={newUser.sendInvitation}
                  onCheckedChange={(checked) => setNewUser((prev) => ({ ...prev, sendInvitation: !!checked }))}
                />
                <Label htmlFor="send_invitation" className="cursor-pointer text-sm">
                  Send invitation email
                </Label>
              </div>
            )}
            <DialogFooter className="col-span-full mt-4 max-md:gap-2">
              <Button type="button" variant="outline" onClick={() => setAddUserDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !newUser.email.trim() ||
                  !newUser.role_id ||
                  (!newUser.deactivateAccess && !newUser.first_name.trim()) ||
                  addUser.isPending
                }
              >
                {addUser.isPending
                  ? "Adding..."
                  : newUser.deactivateAccess
                    ? "Create User"
                    : newUser.sendInvitation
                      ? "Send Invitation"
                      : "Add User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User group assignment dialog */}
      <Dialog open={groupAssignDialogOpen} onOpenChange={setGroupAssignDialogOpen}>
        <DialogContent className="sm:max-w-md max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4 max-md:[overflow-wrap:anywhere]">
          <DialogHeader className="max-md:pr-6">
            <DialogTitle>
              Assign {singularDisplayLabel} — {assigningUser?.name || assigningUser?.email}
            </DialogTitle>
            <DialogDescription>
              Set the primary and secondary {pluralDisplayLabel.toLowerCase()} for this user.
            </DialogDescription>
          </DialogHeader>

          {assignmentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 motion-preset-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-5 py-2">
              {/* Primary group */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Primary {singularDisplayLabel}</Label>
                <Select
                  value={selectedPrimary ?? "none"}
                  onValueChange={(val) => {
                    const newPrimary = val === "none" ? null : val;
                    setSelectedPrimary(newPrimary);
                    if (newPrimary) {
                      setSelectedSecondary((prev) => prev.filter((id) => id !== newPrimary));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`Select primary ${singularDisplayLabel.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">None</span>
                    </SelectItem>
                    {allGroups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Secondary groups */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Secondary {pluralDisplayLabel}</Label>
                {allGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No {pluralDisplayLabel.toLowerCase()} available.</p>
                ) : (
                  <div className="space-y-2 rounded-md border p-3 max-h-48 overflow-y-auto">
                    {allGroups.map((g) => {
                      const isPrimary = selectedPrimary === g.id;
                      return (
                        <div key={g.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`secondary-${g.id}`}
                            checked={selectedSecondary.includes(g.id)}
                            disabled={isPrimary}
                            onCheckedChange={() => toggleSecondary(g.id)}
                          />
                          <label
                            htmlFor={`secondary-${g.id}`}
                            className={`text-sm cursor-pointer ${isPrimary ? "text-muted-foreground" : ""}`}
                          >
                            {g.name}
                            {isPrimary && <span className="ml-2 text-xs text-muted-foreground">(primary)</span>}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="max-md:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setGroupAssignDialogOpen(false);
                setAssigningUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={setAccessGroupAssignments.isPending || assignmentLoading}
              onClick={handleSaveGroupAssignments}
            >
              {setAccessGroupAssignments.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Outlet />
    </div>
  );
}
